import ApiClient from '../../utils/api/ApiClient'
import { UserInvitation } from './type/UserInvitation'
import { Permission } from '../../types/permission'
import { UserType } from '../onboardingContext/OnboardingContextSlice'
import { MembershipEventStatus } from './type/MemberShipEventStatus'

export interface AcceptInvitationRequest {
  token: string
  password?: string
  firstName: string
  lastName: string
  phoneNumber?: string
}

export interface SubmitInvitationRequest {
  email: string
  isPrescriber: boolean
  organizationId: string
  permissions: Permission[]
}

export interface ApproveInvitationRequest {
  requestToken: string
  isApproved: boolean
}

export interface ApproveJoinRequestPayload {
  requestToken: string
  isApproved: boolean
}

interface AcceptInvitationResponse {
  userId: string
}

interface ApproveRequestResponse {
  token: string
  isApproved: boolean
}

interface UserInvitationCreationResponse {
  token: string
  email: string
  inviterName: string
  inviterEmail: string
  expiresAt: string
  status: string
  organizationId: string
  userId?: string
  isPrescriber: boolean
  permissions: Permission[]
}

export interface JoinRequestResponseDto {
  token: string
  userId: string
  email: string
  firstName?: string
  lastName?: string
  organizationId: string
  organizationName: string
  requestedRole: UserType
  requestedPermissions: Permission[]
  status: MembershipEventStatus
  expiresAt: string
  createdAt: string
}

const submitInvitation = async (data: SubmitInvitationRequest) => {
  const response = await ApiClient.post<UserInvitationCreationResponse>(
    `/bff/organization-membership/invitations`,
    data
  )
  return response?.data
}

const getInvitationByToken = async ({
  invitationToken,
}: {
  invitationToken: string
}): Promise<UserInvitation> => {
  const response = await ApiClient.get<UserInvitation>(
    `/bff/organization-membership/invitations/${invitationToken}`
  )
  return response?.data
}

const acceptInvitation = async (
  data: AcceptInvitationRequest
): Promise<AcceptInvitationResponse> => {
  const response = await ApiClient.post(`/bff/organization-membership/invitations/acceptance`, data)
  return response?.data
}

const approveInvitation = async (
  data: ApproveInvitationRequest
): Promise<ApproveRequestResponse> => {
  const response = await ApiClient.post(`/bff/organization-membership/invitations/approval`, data)
  return response?.data
}

export const sendJoinOrganizationRequest = async (requestBody: {
  organizationId: string
  requestedRole: UserType
}): Promise<JoinRequestResponseDto> => {
  const { data } = await ApiClient.post(`bff/organization-membership/join-requests`, requestBody)
  return data
}

const approveJoinRequest = async (
  data: ApproveJoinRequestPayload
): Promise<ApproveRequestResponse> => {
  const response = await ApiClient.post(`/bff/organization-membership/join-requests/approval`, data)
  return response?.data
}

const getJoinRequestByToken = async ({
  token,
}: {
  token: string
}): Promise<JoinRequestResponseDto> => {
  const response = await ApiClient.get<JoinRequestResponseDto>(
    `/bff/organization-membership/join-requests/${token}`
  )
  return response?.data
}

const getPendingJoinRequests = async (): Promise<JoinRequestResponseDto[]> => {
  const response = await ApiClient.get<JoinRequestResponseDto[]>(
    `/bff/organization-membership/join-requests?status=${[
      MembershipEventStatus.PENDING,
      MembershipEventStatus.PENDING_OWNER_APPROVAL,
    ].join(',')}`
  )
  return response?.data
}

const updateJoinRequest = async ({
  token,
  status,
}: {
  token: string
  status: MembershipEventStatus
}) => {
  const response = await ApiClient.patch(`/bff/organization-membership/join-requests/${token}`, {
    status,
  })
  return response?.data
}

export const OrgMembershipApiClient = {
  acceptInvitation,
  approveInvitation,
  approveJoinRequest,
  getInvitationByToken,
  getJoinRequestByToken,
  submitInvitation,
  getPendingJoinRequests,
  updateJoinRequest,
}
